import { create, StateCreator } from "zustand";
import { persist, PersistOptions } from "zustand/middleware";
import useProjectStore from "./ProjectStore";

export type Severity = "success" | "error" | "warning" | "info";

export interface INotification {
  key: string;
  message: string;
  severity: Severity;
  displayed: boolean;
  date: Date;
}

export interface ILoggedNotification {
  key: string;
  message: string;
  severity: Severity;
  date: Date;
  batchID: number | null;
  netContName: string;
  networkName: string;
}

const MaxPersistedNotifications = 100;

type NotificationStore = {
  notifications: INotification[];
  undisplayedNotifications: INotification[];
  persistedNotifications: ILoggedNotification[];
  currentBatchID: number;
  enqueueNotification: (key: string, message: string, severity: Severity, batch?: number) => void;
  removeNotification: (key: string) => void;
  markAsDisplayed: (key: string) => void;
  clearPersistedNotifications: () => void;
  updateCurrentBatchID: () => void;
};

// Define a custom type for the persist function
type MyPersist = (
  config: StateCreator<NotificationStore>,
  options: PersistOptions<NotificationStore>,
) => StateCreator<NotificationStore>;

const useNotificationStore = create<NotificationStore>(
  (persist as MyPersist)(
    (set) => ({
      notifications: [],
      undisplayedNotifications: [],
      persistedNotifications: [],
      currentBatchID: 1,
      enqueueNotification: (key: string, message: string, severity: Severity, batch?: number) => {
        const notification: INotification = {
          key: key,
          message: message,
          severity: severity,
          displayed: false,
          date: new Date(),
        };
        const logNotif: ILoggedNotification = {
          key: notification.key,
          message: notification.message,
          severity: notification.severity,
          date: notification.date,
          batchID: batch ?? null,
          netContName: useProjectStore.getState().activeNetworkContainer?.networkContainerName ?? "",
          networkName: useProjectStore.getState().activeNetwork?.deviceInstances !== undefined && useProjectStore.getState().activeNetwork!.deviceInstances.length > 0 ? useProjectStore.getState().activeNetwork!.deviceInstances[0].deviceTag : "",
        };
        set((state) => ({
          notifications: [...state.notifications, notification],
          undisplayedNotifications: [...state.undisplayedNotifications, notification],
          persistedNotifications:
            state.persistedNotifications.length >= MaxPersistedNotifications
              ? [logNotif].concat(state.persistedNotifications.slice(0, -1))
              : [logNotif, ...state.persistedNotifications],
        }));
      },
      removeNotification: (key) =>
        set((state) => ({
          notifications: [...state.notifications.filter((item) => item.key !== key)],
          undisplayedNotifications: [...state.undisplayedNotifications.filter((item) => item.key !== key)],
        })),
      markAsDisplayed: (key) =>
        set((state) => {
          const updatedNotifications = state.notifications.map((item) => {
            if (item.key === key) {
              return { ...item, displayed: true };
            } else {
              return item;
            }
          });

          return {
            ...state,
            notifications: updatedNotifications,
            undisplayedNotifications: [...state.undisplayedNotifications.filter((item) => item.key !== key)],
          };
        }),
      clearPersistedNotifications: () =>
        set((state) => ({
          currentBatchID: 1,
          persistedNotifications: [],
        })),
      updateCurrentBatchID: () =>
        set((state) => ({
          currentBatchID: state.currentBatchID + 1,
        })),
    }),
    {
      name: "notificationStore", // Unique name for localStorage key
      partialize: (state) => ({ ...state, persistedNotifications: state.persistedNotifications }), // Only persist messages
    },
  ),
);

export default useNotificationStore;
